import { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import type { Area } from 'react-easy-crop';
import { createPortal } from 'react-dom';
import { Check, X, ZoomIn, ZoomOut } from 'lucide-react';

interface ImageCropperProps {
  image: string;
  onConfirm: (croppedImage: string) => void;
  onCancel: () => void;
}

export function ImageCropper({ image, onConfirm, onCancel }: ImageCropperProps) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [processing, setProcessing] = useState(false);

  const onCropComplete = useCallback((_: Area, pixels: Area) => {
    setCroppedAreaPixels(pixels);
  }, []);

  const handleConfirm = async () => {
    if (!croppedAreaPixels) return;
    setProcessing(true);
    try {
      const cropped = await cropImage(image, croppedAreaPixels);
      onConfirm(cropped);
    } catch {
      onCancel();
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[200] bg-black flex flex-col" style={{ paddingTop: 'var(--sat)', paddingBottom: 'var(--sab)' }}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-slate-900/80">
        <button onClick={onCancel} className="p-2 text-slate-400 active:text-white">
          <X size={24} />
        </button>
        <span className="text-sm font-semibold text-slate-200">Crop Photo</span>
        <button
          onClick={handleConfirm}
          disabled={processing}
          className="p-2 text-accent active:text-accent/80 disabled:opacity-50"
        >
          <Check size={24} />
        </button>
      </div>

      {/* Crop area */}
      <div className="flex-1 relative">
        <Cropper
          image={image}
          crop={crop}
          zoom={zoom}
          aspect={1}
          cropShape="round"
          showGrid={false}
          onCropChange={setCrop}
          onZoomChange={setZoom}
          onCropComplete={onCropComplete}
          minZoom={1}
          maxZoom={4}
        />
      </div>

      {/* Zoom control */}
      <div className="flex items-center justify-center gap-4 px-8 py-4 bg-slate-900/80">
        <ZoomOut size={18} className="text-slate-500" />
        <input
          type="range"
          min={1}
          max={4}
          step={0.05}
          value={zoom}
          onChange={(e) => setZoom(Number(e.target.value))}
          className="flex-1 h-1 accent-accent"
        />
        <ZoomIn size={18} className="text-slate-500" />
      </div>
    </div>,
    document.body,
  );
}

/** Crop and resize image to 512x512 using canvas */
async function cropImage(src: string, area: Area): Promise<string> {
  const img = await loadImage(src);
  const canvas = document.createElement('canvas');
  const size = 512;
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;

  ctx.drawImage(
    img,
    area.x, area.y, area.width, area.height,
    0, 0, size, size,
  );

  return canvas.toDataURL('image/jpeg', 0.85);
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}
